import { ACHIEVEMENTS, ACHIEVEMENT_BY_ID, AVATAR_PREFIX } from '~/data/achievements'
import { BOSS_SKILL_IDS } from '~/data/bossSkills'
import { CREATURE_POOL, HOMAGE_AWARDS } from '~/data/creatures'
import { ELITE_MODIFIERS } from '~/data/eliteModifiers'
import { EVENTS } from '~/data/events'
import { ENEMY_ART } from '~/data/enemyArt'
import { RELIC_POOL } from '~/data/relics'
import { TRAINED_SKILL_IDS } from '~/data/trainedSkills'
import { PUZZLE_LEVEL_COUNT } from '~~/shared/puzzleMeta'
import { submitAchievements } from '~/composables/useLeaderboard'

/**
 * 🏆 v5.95: the achievements ledger. Earned ids persist locally and
 * ride the same progress epoch as every other unlock. evaluate() is
 * cheap and idempotent — the title screen calls it on mount.
 *
 * v6.45: table-driven. Every achievement is a `progress()` that reads
 * a counter the game already keeps (the lifetime ledger, the codex's
 * mastered tier, the daily streak, the puzzle bests, ownership) or one
 * of the lifetime `marks` a run-end site sets in one line. `have`
 * against `need` is what the shelf draws as a bar; satisfied means
 * have >= need. Earned dates live beside the ids (a second key, so an
 * old ledger loads unchanged), and the id list syncs through the meta
 * document like the codex — union, never shrinking.
 */
const KEY = 'elemental-dispatch-achievements'
const AT_KEY = 'elemental-dispatch-achievements-at'
// 🎩 v7.78: the homage awakenings whose reveal this device has shown (the title screen's achievement card plays it).
const REVEALED_KEY = 'elemental-dispatch-homage-revealed'

export interface AchievementProgress { have: number; need: number }

const TWENTY_MINUTES = 20 * 60_000
const FIFTY_HOURS = 50

export function useAchievements() {
  const earned = useState<string[]>('achievements-earned', () => [])
  const earnedAt = useState<Record<string, number>>('achievements-earned-at', () => ({}))
  const loaded = useState('achievements-loaded', () => false)
  const revealed = useState<string[]>('achievements-revealed', () => [])

  function load() {
    if (loaded.value || typeof localStorage === 'undefined') return
    try {
      const raw = localStorage.getItem(KEY)
      if (raw) {
        const parsed = JSON.parse(raw)
        if (Array.isArray(parsed)) earned.value = parsed.filter(x => typeof x === 'string')
      }
      const rawAt = localStorage.getItem(AT_KEY)
      if (rawAt) {
        const parsed = JSON.parse(rawAt) as Record<string, unknown>
        if (parsed && typeof parsed === 'object') {
          earnedAt.value = Object.fromEntries(
            Object.entries(parsed).filter(([, v]) => typeof v === 'number' && Number.isFinite(v)) as [string, number][],
          )
        }
      }
      const rawRevealed = localStorage.getItem(REVEALED_KEY)
      if (rawRevealed) {
        const parsed = JSON.parse(rawRevealed)
        if (Array.isArray(parsed)) revealed.value = parsed.filter(x => typeof x === 'string')
      }
    } catch { /* private mode — session-only ledger */ }
    loaded.value = true
  }

  function persist() {
    try {
      localStorage.setItem(KEY, JSON.stringify(earned.value))
      localStorage.setItem(AT_KEY, JSON.stringify(earnedAt.value))
    } catch { /* ok */ }
  }

  function has(id: string): boolean {
    return earned.value.includes(id)
  }

  // ---- the counters --------------------------------------------------
  // Each reads its store through the composable so the shelf's computed
  // tracks the same reactive state the game writes; every load() is a
  // once-per-session no-op after the first.
  function stats() {
    const lifetime = useLifetimeStats()
    lifetime.load()
    return lifetime.stats.value
  }
  function cleared(level: number): AchievementProgress {
    const asc = useAscension()
    asc.load()
    return { have: asc.cleared.value.includes(level) ? 1 : 0, need: 1 }
  }
  function masteredOf(kind: 'enemies' | 'bosses' | 'relics' | 'skills' | 'emods', ids: string[]): AchievementProgress {
    const codex = useCollection()
    codex.load()
    return { have: ids.filter(id => codex.hasMastered(kind, id)).length, need: ids.length }
  }
  function ownedCount(): number {
    const roster = useOwnedCreatures()
    roster.load()
    // 🏆 v7.77: only creatures that can be OWNED count (bought or pulled) - award-only ones (Legacy, the
    // homage five) never can, and counting them made The Last Age ask for 106 of the 101 ownable.
    return roster.owned.value.filter(id => OWNABLE_IDS.has(id)).length
  }
  function streak(): number {
    const daily = useDailyGame()
    daily.load()
    return daily.store.value.streak
  }
  function puzzleBests(): Record<number, number> {
    const puzzle = usePuzzleGame()
    puzzle.load()
    return puzzle.best.value
  }
  /** The best campaign win streak at any ascension of at least `minAsc`. */
  const bestStreakFrom = (minAsc: number) => Math.max(0, ...Object.entries(stats().streaks)
    .filter(([asc]) => Number(asc) >= minAsc).map(([, s]) => s.best))
  const puzzleHighest = () => {
    const best = puzzleBests()
    let lv = 0
    while (lv < PUZZLE_LEVEL_COUNT && best[lv + 1] !== undefined) lv++
    return lv
  }
  const OWNABLE_IDS = new Set(CREATURE_POOL.filter(c => !c.awardOnly).map(c => c.id))
  const ALL_CREATURES = OWNABLE_IDS.size
  const RELIC_IDS = RELIC_POOL.filter(r => !r.banked && !r.homage).map(r => r.id)
  const ENEMY_IDS = Object.keys(ENEMY_ART)
  const EMOD_IDS = Object.keys(ELITE_MODIFIERS)
  const EVENT_IDS = EVENTS.map(e => e.id)

  /** How far along each achievement is RIGHT NOW (whether or not it is banked yet). */
  const PROGRESS: Record<string, () => AchievementProgress> = {
    'first-light': () => ({ have: stats().runs.campaign.won, need: 1 }),
    'bruised': () => cleared(1),
    'scarred': () => cleared(5),
    'short-handed': () => cleared(10),
    'into-the-gauntlet': () => ({ have: stats().marks.gauntletBestAct ?? 0, need: 10 }),
    'the-long-march': () => ({ have: stats().marks.gauntletBestAct ?? 0, need: 25 }),
    // v6.56 (per Javi: "needs rework to be more difficult"): five in a row, at Ascension 5 or above.
    'iron-streak': () => ({ have: bestStreakFrom(5), need: 5 }),
    'hundred-sorties': () => ({ have: stats().missions.succeeded, need: 100 }),
    'thousand-sorties': () => ({ have: stats().missions.succeeded, need: 1000 }),
    'bestiary': () => masteredOf('enemies', ENEMY_IDS),
    'warden-of-ruins': () => masteredOf('bosses', BOSS_SKILL_IDS),
    'relic-hunter': () => masteredOf('relics', RELIC_IDS),
    'curriculum': () => masteredOf('skills', TRAINED_SKILL_IDS),
    'lawbreaker': () => masteredOf('emods', EMOD_IDS),
    'half-the-wilds': () => ({ have: ownedCount(), need: 50 }),
    // 🦕 v5.96 (per Javi): OWNERSHIP, not codex sightings — all 100
    // creatures bought or pulled onto the account's permanent list.
    'legacy-awakens': () => ({ have: ownedCount(), need: ALL_CREATURES }),
    'seven-days': () => ({ have: streak(), need: 7 }),
    'thirty-days': () => ({ have: streak(), need: 30 }),
    'halfway-up': () => ({ have: puzzleHighest(), need: 25 }),
    'clean-sheet': () => ({ have: Object.values(puzzleBests()).filter(d => d === 0).length, need: PUZZLE_LEVEL_COUNT }),
    // 💨 v6.57 (per Javi): at Ascension 8 or above - reads the A8+ mark.
    'like-the-wind': () => ({ have: (stats().marks.fastestWinA8Ms ?? Number.POSITIVE_INFINITY) <= TWENTY_MINUTES ? 1 : 0, need: 1 }),
    'old-timer': () => ({ have: Math.floor(stats().playtimeMs / 3_600_000), need: FIFTY_HOURS }),
    'the-age-returns': () => ({ have: stats().marks.legacyWin ?? 0, need: 1 }),
    // ---- v6.52: the avatar batch. Marks set at the event (useRun, the
    // weekly claim); the collector counts the lifetime event ledger
    // against the catalogue, so a retired event never blocks it.
    'no-quarter-given': () => ({ have: stats().marks.noQuarterWin ?? 0, need: 1 }),
    'untouched': () => ({ have: stats().marks.untouchedAct ?? 0, need: 1 }),
    'pacifist': () => ({ have: stats().marks.pacifistWin ?? 0, need: 1 }),
    'comeback': () => ({ have: stats().marks.brinkBoss ?? 0, need: 1 }),
    'collector-of-tales': () => ({ have: EVENT_IDS.filter(id => stats().eventsSeen.includes(id)).length, need: EVENT_IDS.length }),
    'weekly-podium': () => ({ have: stats().marks.weeklyPodium ?? 0, need: 1 }),
    // ---- 🎗️ v6.54: the homage chains. The two unlocks are "every X
    // mastered" plus Ascension 10 cleared; the ends are marks set at an
    // A8+ win with A10 already cleared (useRun / useLifetimeStats).
    'keepsake-relic': () => {
      const m = masteredOf('emods', EMOD_IDS)
      return { have: (m.have >= m.need ? 1 : 0) + cleared(10).have, need: 2 }
    },
    'keepsake-lesson': () => {
      const m = masteredOf('bosses', BOSS_SKILL_IDS)
      return { have: (m.have >= m.need ? 1 : 0) + cleared(10).have, need: 2 }
    },
    'empty-handed': () => ({ have: stats().marks.emptyHanded ?? 0, need: 1 }),
    'single-lesson': () => ({ have: stats().marks.singleLesson ?? 0, need: 1 }),
    'kept-both': () => ({ have: stats().marks.keptBoth ?? 0, need: 1 }),
    'the-ascetic': () => ({ have: stats().marks.ascetic ?? 0, need: 1 }),
  }

  function progress(id: string): AchievementProgress {
    const fn = PROGRESS[id]
    if (!fn) return { have: 0, need: 1 }
    const p = fn()
    return { have: Math.min(p.have, p.need), need: p.need }
  }

  /** Is the condition met RIGHT NOW (whether or not it is banked yet)? */
  function satisfied(id: string): boolean {
    const fn = PROGRESS[id]
    if (!fn) return false
    const p = fn()
    return p.have >= p.need
  }

  /** Banks every newly-satisfied achievement; returns the fresh ids. */
  function evaluate(): string[] {
    load()
    const fresh: string[] = []
    const now = Date.now()
    for (const def of ACHIEVEMENTS) {
      if (has(def.id) || !satisfied(def.id)) continue
      earned.value.push(def.id)
      earnedAt.value = { ...earnedAt.value, [def.id]: now }
      fresh.push(def.id)
    }
    if (fresh.length > 0) persist()
    // 🏆 v7.99: the trophies board hears the ledger (signed-in only; the server
    // keeps the row only when the count grew, so a repeat visit costs one cached no-op).
    if (earned.value.length > 0) void submitAchievements(earned.value).catch(() => {})
    return fresh
  }

  /** ☁️ Union-merge the ledger from another device. Ids only: a date
   * this device never saw stays unknown (the shelf shows none). */
  function adopt(remote: string[]) {
    load()
    if (!Array.isArray(remote)) return
    const add = remote.filter(id => typeof id === 'string' && ACHIEVEMENT_BY_ID[id] && !earned.value.includes(id))
    if (add.length === 0) return
    earned.value = [...earned.value, ...add]
    persist()
    void submitAchievements(earned.value).catch(() => {})
  }

  /** 🎩 v7.78 (per Javi: "no achievement reveal was given for them"): the homage awakenings in the ledger - banked
   * here or adopted from another device - whose reveal this device has not shown yet, in table order. */
  function unrevealed(): string[] {
    load()
    return HOMAGE_AWARDS.map(([id]) => id).filter(id => earned.value.includes(id) && !revealed.value.includes(id))
  }
  /** The reveal was seen (its card dismissed): never again on this device. */
  function markRevealed(id: string) {
    load()
    if (revealed.value.includes(id)) return
    revealed.value = [...revealed.value, id]
    try { localStorage.setItem(REVEALED_KEY, JSON.stringify(revealed.value)) } catch { /* ok */ }
  }

  /** The titles this player may wear: every earned, title-bearing id. */
  function earnedTitles(): string[] {
    load()
    return ACHIEVEMENTS.filter(a => a.title && earned.value.includes(a.id)).map(a => a.id)
  }
  /** 🖼️ v6.52: the avatars this player may wear, as `ach:<id>` ids. */
  function earnedAvatars(): string[] {
    load()
    return ACHIEVEMENTS.filter(a => a.avatar && earned.value.includes(a.id)).map(a => AVATAR_PREFIX + a.id)
  }

  return { earned, earnedAt, load, has, progress, evaluate, adopt, unrevealed, markRevealed, earnedTitles, earnedAvatars }
}
